import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, Check, PenLine, ChevronDown } from 'lucide-react';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Help, SKILL_STYLE, SKILL_LABEL, TREND_LABEL } from './shared';

const STATUS_OPTS = [
  { v: 'progress', label: 'Fortschritt gemacht', cls: 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' },
  { v: 'no_opportunity', label: 'Keine Gelegenheit diese Woche', cls: 'border-border bg-secondary/40 text-foreground' },
  { v: 'setback', label: 'War schwieriger', cls: 'border-amber-500/50 bg-amber-500/10 text-amber-300' },
];
const OUTCOME_OPTS = [
  { v: 'helped', label: 'Hat geholfen' }, { v: 'partial', label: 'Teilweise' }, { v: 'no_fit', label: 'Nicht mein Ding' },
];

export default function CheckinTab({ gotoTab, onSaved }) {
  const [ctx, setCtx] = useState(null);
  const [form, setForm] = useState({ highlight: '', blocker: '' });
  const [goalStatus, setGoalStatus] = useState({});
  const [goalNotes, setGoalNotes] = useState({});
  const [ideaOutcomes, setIdeaOutcomes] = useState({});
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    api.teamDevCheckinContext().then((res) => {
      const d = res.data;
      setCtx(d);
      const draftKey = `td-checkin-${d.current_week}`;
      const draft = JSON.parse(localStorage.getItem(draftKey) || 'null');
      if (d.existing) {
        setForm({ highlight: d.existing.highlight || '', blocker: d.existing.blocker || '' });
      } else if (draft) {
        setForm({ highlight: draft.highlight || '', blocker: draft.blocker || '' });
      }
      const gs = {};
      (d.my_goals || []).forEach((g) => { if (g.this_week?.status) gs[g.id] = g.this_week.status; });
      setGoalStatus(gs);
    }).catch(() => toast.error('Konnte nicht geladen werden'));
  }, []);

  // Auto-save draft
  useEffect(() => {
    if (!ctx) return;
    const t = setTimeout(() => {
      localStorage.setItem(`td-checkin-${ctx.current_week}`, JSON.stringify(form));
    }, 400);
    return () => clearTimeout(t);
  }, [form, ctx]);

  const save = useCallback(async () => {
    if (!form.highlight.trim()) {
      return toast('Magst du noch dein Highlight ergänzen? Das ist das Einzige, was der Feed braucht. 🌟');
    }
    setSaving(true);
    try {
      const res = await api.teamDevSaveCheckin({
        highlight: form.highlight, blocker: form.blocker,
        goal_updates: Object.entries(goalStatus).map(([goal_id, status]) => ({ goal_id, status, note: goalNotes[goal_id] || '' })),
        idea_outcomes: Object.entries(ideaOutcomes).map(([idea_id, outcome]) => ({ idea_id, outcome })),
      });
      localStorage.removeItem(`td-checkin-${ctx.current_week}`);
      toast.success('Check-in gespeichert — danke dir! Dein Highlight ist im Team-Feed. ✨');
      if (res.data.kudos?.length) toast.success(`💛 Kudos an ${res.data.kudos.join(', ')} ist raus!`);
      onSaved?.();
      gotoTab('home');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern hat nicht geklappt');
    } finally { setSaving(false); }
  }, [form, goalStatus, goalNotes, ideaOutcomes, ctx, gotoTab, onSaved]);

  if (!ctx) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="max-w-2xl space-y-5" data-testid="td-checkin">
      <div className="td-rise bg-card border border-border rounded-lg p-6 space-y-6">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-foreground">Dein Check-in</p>
          <span className="text-[9px] font-mono uppercase border border-border rounded px-1.5 py-0.5 text-muted-foreground">{ctx.current_week}</span>
          {ctx.existing && <span className="text-[9px] font-mono uppercase border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 rounded px-1.5 py-0.5">bereits abgegeben — du kannst nachbessern</span>}
        </div>

        <div>
          <p className="text-xs font-medium text-foreground mb-1.5">Dein Highlight der Woche <span className="text-emerald-400">*</span></p>
          <Textarea value={form.highlight} onChange={(e) => setForm((f) => ({ ...f, highlight: e.target.value }))} rows={3}
            placeholder="Was lief gut? Wer hat dir geholfen? (@Name erwähnen = automatisches Kudos)"
            className="text-sm bg-background rounded-md" data-testid="td-checkin-highlight" />
          <Help>Landet im Team-Feed. Konkret schlägt allgemein: ‚Talus Modell-Walkthrough hat mir 2 Stunden gespart‘ statt ‚gute Woche‘.</Help>
        </div>

        <div>
          <p className="text-xs font-medium text-foreground mb-1.5">Was hat dich gebremst? <span className="text-muted-foreground font-normal">(optional)</span></p>
          <Textarea value={form.blocker} onChange={(e) => setForm((f) => ({ ...f, blocker: e.target.value }))} rows={2}
            placeholder="Prozess, Tool oder Zusammenarbeit — alles erlaubt."
            className="text-sm bg-background rounded-md" data-testid="td-checkin-blocker" />
          <Help>Sichtbar fürs Core-Team. Beschreibe das Problem, nicht den Schuldigen: ‚Die Pipeline-Übergabe war unklar‘ statt ‚Max hat schlecht übergeben‘.</Help>
        </div>

        <div>
          <p className="text-xs font-medium text-foreground mb-1.5">Möchtest du jemandem diese Woche Feedback geben? <span className="text-muted-foreground font-normal">(optional)</span></p>
          <button onClick={() => gotoTab('give')} data-testid="td-checkin-feedback-btn"
            className="flex items-center gap-1.5 text-xs font-medium text-emerald-400 border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/15 rounded-full px-4 py-2 transition-colors">
            <PenLine className="w-3.5 h-3.5" /> Ja, Feedback schreiben
          </button>
          <Help>Optional — leer lassen ist völlig okay. Lieber kein Feedback als erzwungenes.</Help>
        </div>

        {(ctx.my_goals || []).length > 0 && (
          <div className="space-y-4">
            <p className="text-xs font-medium text-foreground">Deine Ziele diese Woche <span className="text-emerald-400">*</span></p>
            {ctx.my_goals.map((g) => (
              <div key={g.id} className="border border-border rounded-md p-3.5 space-y-2.5 bg-background/50" data-testid={`td-checkin-goal-${g.id}`}>
                <p className="text-sm text-foreground flex items-center gap-2 flex-wrap">
                  {g.title}
                  <span className={`text-[8px] font-mono uppercase border rounded-full px-1.5 py-px ${SKILL_STYLE[g.skill_type]}`}>{SKILL_LABEL[g.skill_type]}</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {STATUS_OPTS.map((o) => (
                    <button key={o.v} onClick={() => setGoalStatus((s) => ({ ...s, [g.id]: o.v }))}
                      data-testid={`td-goal-status-${g.id}-${o.v}`}
                      className={`text-xs rounded-full border px-3 py-1.5 transition-colors ${goalStatus[g.id] === o.v ? o.cls : 'border-border text-muted-foreground hover:text-foreground hover:bg-secondary/50'}`}>
                      {goalStatus[g.id] === o.v && <Check className="w-3 h-3 inline mr-1" />}{o.label}
                    </button>
                  ))}
                </div>
                {goalStatus[g.id] === 'setback' && (
                  <div className="td-pop">
                    <Textarea value={goalNotes[g.id] || ''} onChange={(e) => setGoalNotes((s) => ({ ...s, [g.id]: e.target.value }))} rows={2}
                      placeholder="Was war schwierig? (nur für dich sichtbar, hilft dir beim Rückblick)"
                      className="text-sm bg-background rounded-md" data-testid={`td-goal-note-${g.id}`} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {(ctx.try_ideas || []).length > 0 && ctx.try_ideas.map((idea) => (
          <div key={idea.id} className="border border-violet-500/25 rounded-md p-3.5 bg-violet-500/5 space-y-2" data-testid={`td-try-idea-${idea.id}`}>
            <p className="text-sm text-foreground">Du wolltest ausprobieren: „{idea.text}“ — wie lief’s?</p>
            <div className="flex flex-wrap gap-2">
              {OUTCOME_OPTS.map((o) => (
                <button key={o.v} onClick={() => setIdeaOutcomes((s) => ({ ...s, [idea.id]: o.v }))}
                  data-testid={`td-idea-outcome-${idea.id}-${o.v}`}
                  className={`text-xs rounded-full border px-3 py-1.5 transition-colors ${ideaOutcomes[idea.id] === o.v ? 'border-violet-500/50 bg-violet-500/15 text-violet-300' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                  {ideaOutcomes[idea.id] === o.v && <Check className="w-3 h-3 inline mr-1" />}{o.label}
                </button>
              ))}
            </div>
            <Help>Nur du siehst deine Antwort — sie hilft dir beim Rückblick.</Help>
          </div>
        ))}

        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6" data-testid="td-checkin-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
          {ctx.existing ? 'Aktualisieren' : 'Check-in abschicken'}
        </Button>
      </div>

      {(ctx.history || []).filter((h) => h.week_key !== ctx.current_week).length > 0 && (
        <div>
          <button onClick={() => setShowHistory((s) => !s)} data-testid="td-checkin-history-toggle"
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showHistory ? '' : '-rotate-90'}`} />
            Frühere Wochen ({ctx.history.filter((h) => h.week_key !== ctx.current_week).length})
          </button>
          {showHistory && (
            <div className="mt-2 space-y-2">
              {ctx.history.filter((h) => h.week_key !== ctx.current_week).map((c, i) => (
                <div key={c.week_key} className="bg-card border border-border rounded-lg p-4" data-testid={`td-checkin-history-${i}`}>
                  <p className="text-[10px] font-mono text-muted-foreground mb-1">{c.week_key}</p>
                  {c.highlight && <p className="text-xs text-foreground/90"><span className="mr-1">🌟</span>{c.highlight}</p>}
                  {c.blocker && <p className="text-xs text-foreground/90 mt-0.5"><span className="mr-1">🧗</span>{c.blocker}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export { TREND_LABEL };
